import React from 'react';
import { Link } from 'react-router-dom';
import dayjs from 'dayjs';
import { Avatar, Box, Card, Chip, LinearProgress, Typography } from '@mui/material';
import { MdCheck } from 'react-icons/md';

import RunMapView from '@features/mapVisualization/components/RunMapView';
import RunDriverMarker from '@features/mobileService/components/RunDriverMarker';
import { classifyRunStopStatus, type RunMapStop } from '@features/mapVisualization/runTypes';
import RunStatusChip from '@components/Badges/RunStatusChip';

// A parts-delivery stop is "done" when completed, and "active" while the
// driver is heading to or parked at it (mobility_stops status vocabulary).
const isStopDone = (s: any) => s.status === 'completed';
const isStopActive = (s: any) => s.status === 'in_transit_to' || s.status === 'arrived';

const getInitials = (name?: string | null) => {
  if (!name) return '';
  const parts = name.trim().split(/\s+/);
  if (parts.length >= 2) return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
  return name.slice(0, 2).toUpperCase();
};

interface ActiveRunCardProps {
  run: any;
}

/**
 * Rich card for an in-progress parts delivery run: driver/vehicle header,
 * stop-completion progress, the ordered stop list with per-stop status, and a
 * live map with the driver's current GPS position (via RunDriverMarker).
 */
const ActiveRunCard: React.FC<ActiveRunCardProps> = ({ run }) => {
  const stops: any[] = run.stops || [];
  const total = stops.length;
  const completed = stops.filter(isStopDone).length;
  const progress = total > 0 ? (completed / total) * 100 : 0;

  // Show the assigned driver after accept, else the offered-to driver from the
  // active (non-deprecated) bundle's latest offer (TARGETED offer in flight).
  // Mirrors mobile service (RunDetailPage displayDriver fallback).
  const activeOffer = run?.scenario?.bundletoscenarios?.[0]?.bundle?.offers?.[0] ?? null;
  const offeredDriver = activeOffer?.driver ?? null;
  const displayDriver = run.driver ?? offeredDriver;
  const driverName = displayDriver?.user?.display_name || 'Unassigned';
  const isAccepted = activeOffer?.status === 'assigned' || activeOffer?.status === 'accepted';
  const isOnline = displayDriver?.status === 'online';
  const vehicleLabel = run.vehicle
    ? [run.vehicle.make, run.vehicle.model].filter(Boolean).join(' ')
    : null;
  const plate = run.vehicle?.license_plate;
  const eta = run.expected_return_time ? dayjs(run.expected_return_time).format('h:mm A') : null;

  // The first not-yet-completed stop is the one the driver is working now.
  const currentIdx = stops.findIndex(s => !isStopDone(s));

  const mapStops: RunMapStop[] = stops
    .filter(s => s.address_lat != null && s.address_lng != null)
    .map(s => ({
      id: s.id,
      stopOrder: s.stop_order,
      status: s.status,
      phase: classifyRunStopStatus(s.status),
      address: s.address,
      lat: Number(s.address_lat),
      lng: Number(s.address_lng),
    }));

  const mapId = `parts-run-${run.id}`;
  const defaultCenter = mapStops[0] ? { lat: mapStops[0].lat, lng: mapStops[0].lng } : undefined;

  return (
    <Card elevation={1} sx={{ overflow: 'hidden' }}>
      {/* Header: driver + vehicle + status/ETA */}
      <Box
        component={Link}
        to={`/parts-delivery/runs/${run.id}`}
        sx={{ display: 'flex', alignItems: 'center', gap: 1.5, p: 2, textDecoration: 'none', color: 'inherit' }}
      >
        <Avatar sx={{ bgcolor: 'secondaryAccent.main', fontWeight: 700 }}>
          {getInitials(driverName)}
        </Avatar>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap' }}>
            <Typography variant='body1' sx={{ fontWeight: 700 }} noWrap>
              {driverName}
            </Typography>
            {displayDriver ? (
              <>
                <Chip
                  label={isAccepted ? 'Accepted' : 'Offered'}
                  size='small'
                  variant='outlined'
                  color={isAccepted ? 'success' : 'info'}
                  sx={{ fontSize: 10, height: 18 }}
                />
                {displayDriver.status ? (
                  <Chip
                    label={isOnline ? 'Online' : 'Offline'}
                    size='small'
                    variant='outlined'
                    color={isOnline ? 'success' : 'default'}
                    sx={{ fontSize: 10, height: 18 }}
                  />
                ) : null}
              </>
            ) : null}
          </Box>
          <Typography variant='caption' color='text.secondary' noWrap>
            {vehicleLabel || 'No vehicle'}{plate ? ` -- ${plate}` : ''}
          </Typography>
        </Box>
        <Box sx={{ textAlign: 'right', flexShrink: 0 }}>
          <RunStatusChip status={run.status} sx={{ fontWeight: 600, mb: eta ? 0.5 : 0 }} />
          {eta ? (
            <>
              <Typography variant='caption' color='text.secondary' sx={{ display: 'block', lineHeight: 1.1 }}>
                Return ETA
              </Typography>
              <Typography variant='body2' sx={{ fontWeight: 700 }}>
                {eta}
              </Typography>
            </>
          ) : null}
        </Box>
      </Box>

      {/* Progress bar */}
      <Box sx={{ px: 2, pb: 1.5, display: 'flex', alignItems: 'center', gap: 1.5 }}>
        <LinearProgress
          variant='determinate'
          value={progress}
          sx={{ flex: 1, height: 6, borderRadius: 3 }}
        />
        <Typography variant='caption' sx={{ fontWeight: 700 }}>
          {completed}/{total}
        </Typography>
      </Box>

      {/* Stop list */}
      <Box sx={{ px: 2, pb: 1 }}>
        {stops.map((s, idx) => {
          const orders: any[] = s.delivery_orders || [];
          const business = orders[0]?.recipient_business || s.address || `Stop ${s.stop_order}`;
          const pkgs = orders.reduce((n, o) => n + (o.quantity || 1), 0);
          const orderNums = orders.map(o => o.order_number).filter(Boolean).join(', ');
          const done = isStopDone(s);
          const active = idx === currentIdx || isStopActive(s);

          return (
            <Box key={s.id} sx={{ display: 'flex', alignItems: 'center', gap: 1.5, py: 0.75 }}>
              {done ? (
                <Avatar sx={{ width: 24, height: 24, bgcolor: 'success.main' }}>
                  <MdCheck size={14} color='#fff' />
                </Avatar>
              ) : (
                <Avatar
                  sx={{
                    width: 24,
                    height: 24,
                    fontSize: 12,
                    fontWeight: 700,
                    bgcolor: active ? 'info.main' : 'grey.300',
                    color: active ? '#fff' : 'text.secondary',
                  }}
                >
                  {s.stop_order}
                </Avatar>
              )}
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography
                  variant='body2'
                  noWrap
                  sx={{
                    fontWeight: 600,
                    textDecoration: done ? 'line-through' : 'none',
                    color: done ? 'text.disabled' : 'text.primary',
                  }}
                >
                  {business}
                </Typography>
                <Typography variant='caption' color='text.secondary' noWrap sx={{ display: 'block' }}>
                  {orderNums}{pkgs ? ` -- ${pkgs} pkg` : ''}
                </Typography>
              </Box>
              {done ? (
                <Chip label='Delivered' size='small' color='success' variant='outlined' sx={{ fontSize: 11, height: 22 }} />
              ) : active ? (
                <Chip label='En Route' size='small' color='info' sx={{ fontSize: 11, height: 22 }} />
              ) : null}
            </Box>
          );
        })}
      </Box>

      {/* Live tracking map */}
      {mapStops.length > 0 ? (
        <Box sx={{ height: 280, position: 'relative' }}>
          <RunMapView mapId={mapId} stops={mapStops} defaultCenter={defaultCenter} defaultZoom={12}>
            <RunDriverMarker runId={run.id} mapId={mapId} />
          </RunMapView>
        </Box>
      ) : null}
    </Card>
  );
};

export default ActiveRunCard;
